import React, { useCallback, useEffect, useState } from 'react';
import {
  Award,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  FileText,
  Search,
  ShieldAlert,
  Timer,
  UsersRound
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import {
  DetailItem,
  DetailModalShell,
  EmptyState,
  ErrorBanner,
  MetricCard,
  PageSkeleton,
  StatusBadge,
  extractError,
  formatDateTime,
  formatNumber,
  inputClass,
  labelize
} from './hiringUi';

const typeOptions = [
  { value: 'all', label: 'All types' },
  { value: 'technical', label: 'Technical' },
  { value: 'behavioral', label: 'Behavioral' },
  { value: 'cognitive', label: 'Cognitive' },
  { value: 'personality', label: 'Personality' },
  { value: 'situational', label: 'Situational' },
  { value: 'custom', label: 'Custom' }
];

const activeOptions = [
  { value: 'all', label: 'All states' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' }
];

const attemptOptions = [
  { value: 'all', label: 'All attempts' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'timed_out', label: 'Timed out' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' }
];

const sortOptions = [
  { value: 'updated_at', label: 'Recently updated' },
  { value: 'created_at', label: 'Newest' },
  { value: 'title', label: 'Title' },
  { value: 'attempts', label: 'Attempts' },
  { value: 'average_score', label: 'Average score' }
];

function AssessmentDetails({ detail }) {
  const assessment = detail?.assessment;
  const attempts = detail?.attempts || [];
  if (!assessment) return null;

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Assessment setup</h3>
          </div>
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <DetailItem label="Type" value={labelize(assessment.type)} />
            <DetailItem label="State" value={assessment.isActive ? 'Active' : 'Inactive'} />
            <DetailItem label="Questions" value={formatNumber(assessment.questionCount)} />
            <DetailItem label="Time limit" value={assessment.timeLimitMinutes ? `${formatNumber(assessment.timeLimitMinutes)} minutes` : 'Not timed'} />
            <DetailItem label="Passing score" value={assessment.passingScore === null ? null : `${assessment.passingScore}%`} />
            <DetailItem label="Max attempts" value={formatNumber(assessment.maxAttempts)} />
            <DetailItem label="Result visibility" value={labelize(assessment.resultVisibility)} />
            <DetailItem label="Created" value={formatDateTime(assessment.createdAt)} />
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-100 bg-white">
          <div className="border-b border-slate-100 px-4 py-3">
            <h3 className="font-semibold text-slate-900">Recent attempts</h3>
          </div>
          {!attempts.length ? (
            <div className="p-4 text-sm text-slate-500">No attempts have been assigned or submitted for this assessment.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[780px] divide-y divide-slate-100">
                <thead className="bg-slate-50/50">
                  <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                    <th className="px-4 py-3">Candidate</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Score</th>
                    <th className="px-4 py-3">Warnings</th>
                    <th className="px-4 py-3">Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {attempts.map((attempt) => (
                    <tr key={attempt.id}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">{attempt.candidate?.name || 'Candidate'}</p>
                        <p className="mt-1 text-xs text-slate-500">{attempt.candidate?.email || 'No email'}</p>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={attempt.status} /></td>
                      <td className="px-4 py-3 text-sm text-slate-700">{attempt.percentageScore === null ? 'Not scored' : `${Math.round(attempt.percentageScore)}%`}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{formatNumber(attempt.warningCount)} / {formatNumber(attempt.warningLimit)}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{formatDateTime(attempt.submittedAt || attempt.startedAt || attempt.assignedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <aside className="space-y-5">
        <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">Role</h3>
          <div className="mt-3 space-y-3">
            <DetailItem label="Job" value={assessment.job?.title} />
            <DetailItem label="Employer" value={assessment.employer?.companyName} />
            <DetailItem label="Job status" value={labelize(assessment.job?.status)} />
            <DetailItem label="Employer KYC" value={labelize(assessment.employer?.verificationStatus)} />
          </div>
        </section>
        <section className="rounded-[24px] border border-slate-100 bg-slate-50 p-4">
          <h3 className="font-semibold text-slate-900">Attempt summary</h3>
          <div className="mt-3 space-y-3">
            <DetailItem label="Total attempts" value={formatNumber(assessment.attemptSummary?.total)} />
            <DetailItem label="Completed" value={formatNumber((assessment.attemptSummary?.submitted || 0) + (assessment.attemptSummary?.reviewed || 0))} />
            <DetailItem label="Passed" value={formatNumber(assessment.attemptSummary?.passed)} />
            <DetailItem label="Average score" value={assessment.attemptSummary?.averageScore === null ? null : `${Math.round(assessment.attemptSummary?.averageScore || 0)}%`} />
            <DetailItem label="Warnings" value={formatNumber(assessment.attemptSummary?.warnings)} />
          </div>
        </section>
      </aside>
    </div>
  );
}

function AssessmentsListPage() {
  const [assessments, setAssessments] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ total: 0, limit: 25, offset: 0, hasMore: false });
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    active: 'all',
    attemptStatus: 'all',
    sortBy: 'updated_at',
    sortOrder: 'DESC'
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadAssessments = useCallback(async ({ offset = 0, quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await adminAPI.getAdminAssessments({ ...filters, limit: pagination.limit, offset });
      setAssessments(response.assessments || []);
      setStats(response.stats || {});
      setPagination(response.pagination || { total: 0, limit: pagination.limit, offset, hasMore: false });
    } catch (loadError) {
      setError(extractError(loadError, 'Assessments could not be loaded.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, pagination.limit]);

  useEffect(() => {
    loadAssessments({ offset: 0 });
  }, [loadAssessments]);

  const openDetails = async (assessment) => {
    setSelected(assessment);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await adminAPI.getAdminAssessment(assessment.id));
    } catch (detailError) {
      setError(extractError(detailError, 'Assessment details could not be loaded.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({ search: '', type: 'all', active: 'all', attemptStatus: 'all', sortBy: 'updated_at', sortOrder: 'DESC' });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setFilters((current) => ({ ...current, search: searchInput.trim() }));
  };

  if (loading) return <PageSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1400px] space-y-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Assessment operations</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Platform-wide assessment visibility across employer-created assessments, candidate attempts, scoring, review state, warnings, and decision deadlines.
            </p>
          </div>
          
        </div>

        <ErrorBanner message={error} />

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard icon={ClipboardCheck} label="Assessments" value={formatNumber(stats.total)} detail={`${formatNumber(stats.active)} active`} />
          <MetricCard icon={UsersRound} label="Attempts" value={formatNumber(stats.attempts)} detail="All candidate attempts" />
          <MetricCard icon={CheckCircle2} label="Completed" value={formatNumber(stats.completedAttempts)} detail="Submitted or reviewed" />
          <MetricCard icon={Award} label="Passed" value={formatNumber(stats.passedAttempts)} detail={stats.averageScore === null ? 'No score average' : `${Math.round(stats.averageScore || 0)}% average`} />
          <MetricCard icon={ShieldAlert} label="Loaded rows" value={formatNumber(assessments.length)} detail={`${formatNumber(pagination.total)} matching filters`} />
        </div>

        <section className="overflow-hidden rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-4 sm:p-5">
            <form onSubmit={submitSearch} className="flex min-w-0 items-center gap-2 overflow-x-auto pb-1">
              <label className="relative block min-w-[280px] flex-[1_0_320px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input className={`${inputClass} w-full pl-9`} value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search assessment, role, company..." />
              </label>
              <select className={`${inputClass} w-[150px] shrink-0`} value={filters.type} onChange={(event) => setFilters((current) => ({ ...current, type: event.target.value }))}>
                {typeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[145px] shrink-0`} value={filters.active} onChange={(event) => setFilters((current) => ({ ...current, active: event.target.value }))}>
                {activeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[165px] shrink-0`} value={filters.attemptStatus} onChange={(event) => setFilters((current) => ({ ...current, attemptStatus: event.target.value }))}>
                {attemptOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select className={`${inputClass} w-[165px] shrink-0`} value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))}>
                {sortOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <button type="submit" className="inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
                Search
              </button>
            </form>
          </div>

          {!assessments.length ? (
            <div className="p-5">
              <EmptyState title="No assessments found" copy="Adjust search or filters to review assessment activity." icon={FileText} onReset={resetFilters} />
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto xl:block">
                <table className="min-w-[1260px] divide-y divide-slate-100">
                  <thead className="bg-slate-50/50">
                    <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <th className="px-6 py-4">Assessment</th>
                      <th className="px-6 py-4">Employer</th>
                      <th className="px-6 py-4">Setup</th>
                      <th className="px-6 py-4">Attempts</th>
                      <th className="px-6 py-4">Score</th>
                      <th className="px-6 py-4">Updated</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {assessments.map((assessment) => (
                      <tr key={assessment.id} className="align-top transition hover:bg-slate-50/80">
                        <td className="px-6 py-4">
                          <p className="max-w-[280px] truncate font-semibold text-slate-900">{assessment.title}</p>
                          <p className="mt-1 text-sm text-slate-500">{labelize(assessment.type)}</p>
                          <div className="mt-2"><StatusBadge status={assessment.isActive ? 'active' : 'inactive'} /></div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-800">{assessment.employer?.companyName || 'Employer not set'}</p>
                          <p className="mt-1 text-sm text-slate-500">{assessment.job?.title || 'Untitled role'}</p>
                          <p className="mt-1 text-xs text-slate-500">{labelize(assessment.job?.status)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{formatNumber(assessment.questionCount)} questions · {formatNumber(assessment.timeLimitMinutes)} min</p>
                          <p className="mt-1 text-xs text-slate-500">Pass mark {assessment.passingScore === null ? 'not set' : `${assessment.passingScore}%`}</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-semibold text-slate-900">{formatNumber(assessment.attemptSummary?.total)} total</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(assessment.attemptSummary?.inProgress)} active, {formatNumber(assessment.attemptSummary?.reviewed)} reviewed</p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-slate-700">{assessment.attemptSummary?.averageScore === null ? 'Not scored' : `${Math.round(assessment.attemptSummary?.averageScore || 0)}% average`}</p>
                          <p className="mt-1 text-xs text-slate-500">{formatNumber(assessment.attemptSummary?.passed)} passed · {formatNumber(assessment.attemptSummary?.warnings)} warnings</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">{formatDateTime(assessment.updatedAt)}</td>
                        <td className="px-6 py-4 text-right">
                          <button type="button" onClick={() => openDetails(assessment)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Details">
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid gap-3 p-4 xl:hidden">
                {assessments.map((assessment) => (
                  <article key={assessment.id} className="rounded-[24px] border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="truncate font-semibold text-slate-900">{assessment.title}</h2>
                        <p className="mt-1 text-sm text-slate-500">{assessment.job?.title || 'Untitled role'}</p>
                      </div>
                      <StatusBadge status={assessment.isActive ? 'active' : 'inactive'} />
                    </div>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <p className="flex items-center gap-2 text-sm text-slate-600"><ClipboardCheck className="h-4 w-4 text-slate-400" /> {labelize(assessment.type)}</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><Timer className="h-4 w-4 text-slate-400" /> {formatNumber(assessment.timeLimitMinutes)} min</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><UsersRound className="h-4 w-4 text-slate-400" /> {formatNumber(assessment.attemptSummary?.total)} attempts</p>
                      <p className="flex items-center gap-2 text-sm text-slate-600"><Award className="h-4 w-4 text-slate-400" /> {assessment.attemptSummary?.averageScore === null ? 'Not scored' : `${Math.round(assessment.attemptSummary?.averageScore || 0)}% average`}</p>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button type="button" onClick={() => openDetails(assessment)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="View Details">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">
                  Showing {formatNumber(pagination.offset + 1)} - {formatNumber(pagination.offset + assessments.length)} of {formatNumber(pagination.total)}
                </p>
                <div className="flex gap-2">
                  <button type="button" disabled={pagination.offset <= 0 || refreshing} onClick={() => loadAssessments({ offset: Math.max(0, pagination.offset - pagination.limit), quiet: true })} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Previous
                  </button>
                  <button type="button" disabled={!pagination.hasMore || refreshing} onClick={() => loadAssessments({ offset: pagination.offset + pagination.limit, quiet: true })} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {selected && (
        <DetailModalShell
          title={selected.title || 'Assessment'}
          subtitle={`${selected.employer?.companyName || 'Employer'} · ${selected.job?.title || 'Role'}`}
          loading={detailLoading}
          onClose={() => {
            setSelected(null);
            setDetail(null);
          }}
        >
          <AssessmentDetails detail={detail} />
        </DetailModalShell>
      )}
    </div>
  );
}

export default AssessmentsListPage;
